import React, { useState, useEffect, useRef } from 'react';
import { useReactMediaRecorder } from 'react-media-recorder';
import { Modal, Box, Button, Typography, Select, MenuItem, LinearProgress, CircularProgress } from '@mui/material';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase/firebase';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';

const RecordContentModal = ({ open, onClose, onContentRecorded, courseName }) => {
  const [recordingType, setRecordingType] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const videoPreviewRef = useRef(null);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  const {
    status: audioStatus,
    startRecording: startAudioRecording,
    stopRecording: stopAudioRecording,
    mediaBlobUrl: audioBlob
  } = useReactMediaRecorder({ audio: true, mediaType: 'audio/mp3' });

  const {
    status: videoStatus,
    startRecording: startVideoRecording,
    stopRecording: stopVideoRecording,
    mediaBlobUrl: videoBlob
  } = useReactMediaRecorder({ video: true, audio: true, mediaType: 'video/mp4' });

  const {
    status: screenStatus,
    startRecording: startScreenRecording,
    stopRecording: stopScreenRecording,
    mediaBlobUrl: screenBlob
  } = useReactMediaRecorder({ screen: true, audio: true, mediaType: 'video/mp4' });

  useEffect(() => {
    if (recordingType === 'video' && videoPreviewRef.current) {
      navigator.mediaDevices.getUserMedia({ video: true })
        .then(stream => {
          videoPreviewRef.current.srcObject = stream;
        })
        .catch(err => console.error('Error accessing camera:', err));

      const videoElement = videoPreviewRef.current;
      return () => {
        if (videoElement?.srcObject) {
          videoElement.srcObject.getTracks().forEach(track => track.stop());
        }
      };
    }
  }, [recordingType]);

  const handleStartRecording = () => {
    switch (recordingType) {
      case 'audio':
        startAudioRecording();
        break;
      case 'video':
        startVideoRecording();
        break;
      case 'screen':
        startScreenRecording();
        break;
      default:
        break;
    }
  };

  const handleStopRecording = () => {
    switch (recordingType) {
      case 'audio':
        stopAudioRecording();
        break;
      case 'video':
        stopVideoRecording();
        if (videoPreviewRef.current?.srcObject) {
          videoPreviewRef.current.srcObject.getTracks().forEach(track => track.stop());
          videoPreviewRef.current.srcObject = null;
        }
        break;
      case 'screen':
        stopScreenRecording();
        break;
      default:
        break;
    }
  };

  const processVideo = async (blob) => {
    setCompressionProgress(0);
    const ffmpeg = new FFmpeg();
    await ffmpeg.load();
    
    ffmpeg.on('progress', ({ progress }) => {
      setCompressionProgress(Math.round(progress * 100));
    });

    const inputFileName = 'input.mp4';
    const outputFileName = 'output.mp4';
    
    await ffmpeg.writeFile(inputFileName, await fetchFile(blob));
    await ffmpeg.exec([
      '-i', inputFileName,
      '-vf', 'scale=-1:960', // Scale to 960p height while maintaining aspect ratio
      '-r', '30', // Set framerate to 30fps
      '-c:v', 'libx264', // Use H.264 codec
      '-crf', '23', // Constant Rate Factor (18-28 is good, lower means better quality)
      '-preset', 'medium', // Encoding speed preset
      '-c:a', 'aac', // Audio codec
      '-b:a', '128k', // Audio bitrate
      outputFileName
    ]);

    // Read the processed file
    const processedData = await ffmpeg.readFile(outputFileName);
    setCompressionProgress(100);
    return new Blob([processedData], { type: 'video/mp4' });
  };

  const handleUpload = async () => {
    setIsProcessing(true); // Start processing
    try {
      const blob = recordingType === 'audio' ? audioBlob : 
                  recordingType === 'video' ? videoBlob : screenBlob;

      if (!blob) {
        console.error('No recording blob found');
        return;
      }

      // Reset upload progress
      setUploadProgress(0);

      // Convert the mediaBlobUrl to actual blob
      const response = await fetch(blob);
      if (!response.ok) throw new Error('Failed to fetch recording');
      const blobFile = await response.blob();
      
      let processedBlob = blobFile;
      if (recordingType === 'video') {
        processedBlob = await processVideo(blobFile);
      }

      const fileName = `${recordingType}_${Date.now()}.${recordingType === 'audio' ? 'mp3' : 'mp4'}`;
      const storageRef = ref(storage, `recordings/${courseName}/${fileName}`);
      
      // Create upload task
      const uploadTask = uploadBytesResumable(storageRef, processedBlob);

      // Monitor upload progress
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => {
          console.error('Upload error:', error);
          alert('Upload failed. Please try again.');
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            onContentRecorded({
              type: recordingType === 'audio' ? 'audio' : 'firebaseVideo',
              url: downloadURL
            });
            setUploadProgress(100);
            setTimeout(() => onClose(), 1000); // Give time to see 100% progress
          } catch (error) {
            console.error('Error getting download URL:', error);
            alert('Failed to complete upload. Please try again.');
          }
        }
      );
    } catch (error) {
      console.error('Upload process error:', error);
      alert('Error processing upload. Please try again.');
    } finally {
      setIsProcessing(false); // End processing
    }
  };

  const handleDownload = (blobUrl) => {
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = `recording_${Date.now()}.${recordingType === 'audio' ? 'mp3' : 'mp4'}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderVideoPreview = () => {
    if (recordingType === 'video') {
      return (
        <Box sx={{ mt: 2, mb: 2, width: '100%' }}>
          {videoBlob ? (
            <video 
              key={videoBlob}
              src={videoBlob} 
              controls 
              style={{ width: '100%', maxHeight: '200px' }} 
            />
          ) : (
            <video 
              ref={videoPreviewRef}
              autoPlay 
              playsInline
              muted 
              style={{ width: '100%', maxHeight: '200px' }} 
            />
          )}
        </Box>
      );
    }
    return null;
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 400,
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 4,
      }}>
        <Typography variant="h6" gutterBottom>Record Content</Typography>
        
        <Select
          fullWidth
          value={recordingType || ''}
          onChange={(e) => setRecordingType(e.target.value)}
          sx={{ mb: 2 }}
        >
          <MenuItem value="audio">Audio Recording</MenuItem>
          <MenuItem value="video">Video Recording</MenuItem>
          <MenuItem value="screen">Screen Recording</MenuItem>
        </Select>

        {renderVideoPreview()}

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Button 
            variant="contained" 
            onClick={handleStartRecording}
            disabled={!recordingType || ['recording'].includes(
              recordingType === 'audio' ? audioStatus :
              recordingType === 'video' ? videoStatus :
              screenStatus
            )}
          >
            Start Recording
          </Button>
          <Button 
            variant="contained" 
            onClick={handleStopRecording}
            disabled={!recordingType || ['idle', 'stopped'].includes(
              recordingType === 'audio' ? audioStatus :
              recordingType === 'video' ? videoStatus :
              screenStatus
            )}
          >
            Stop Recording
          </Button>
        </Box>

        {(audioBlob || videoBlob || screenBlob) && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="contained" onClick={handleUpload} fullWidth>
              Upload Recording
            </Button>
            <Button 
              variant="contained" 
              onClick={() => handleDownload(recordingType === 'audio' ? audioBlob : recordingType === 'video' ? videoBlob : screenBlob)} 
              fullWidth
            >
              Download Recording
            </Button>
          </Box>
        )}

        {(recordingType === 'video' || recordingType === 'screen') && compressionProgress > 0 && (
          <Box sx={{ width: '100%', mt: 2 }}>
            <LinearProgress variant="determinate" value={compressionProgress} />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Compressing Video: {compressionProgress}%
            </Typography>
          </Box>
        )}

        {uploadProgress > 0 && (
          <Box sx={{ width: '100%', mt: 2 }}>
            <LinearProgress variant="determinate" value={uploadProgress} />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Upload Progress: {Math.round(uploadProgress)}%
            </Typography>
          </Box>
        )}

        {isProcessing && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>Processing recording...</Typography>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default RecordContentModal;